import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiDialogModule } from 'omnium-ui/dialog';
import { OuiDatePicker } from 'omnium-ui/form-field';
import { OuiIconModule } from 'omnium-ui/icon';
import moment, { Moment } from 'moment';

export type BouquetServiceActivationDialogData = {
  consultantId: number;
  consultantLabel: string;
  currentEndDate?: string | null;
};

export type BouquetServiceActivationDialogResult = {
  endDate: string;
};

@Component({
  selector: 'dbw-bouquet-service-activation-dialog',
  imports: [ReactiveFormsModule, OuiButtonModule, OuiDialogModule, OuiDatePicker, OuiIconModule],
  templateUrl: './bouquet-service-activation-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './bouquet-service-activation-dialog.component.scss'
})
export class BouquetServiceActivationDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<BouquetServiceActivationDialogComponent>);
  private readonly data = inject<BouquetServiceActivationDialogData>(MAT_DIALOG_DATA);

  protected readonly consultantId = this.data.consultantId;
  protected readonly consultantLabel = this.data.consultantLabel;
  protected readonly endDateControl = new FormControl<Moment | null>(
    parseDateValue(this.data.currentEndDate) ?? today(),
    { validators: [Validators.required] }
  );
  protected get canSubmit(): boolean {
    return this.endDateControl.valid;
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected confirm(): void {
    if (!this.endDateControl.valid) {
      this.endDateControl.markAsTouched();
      return;
    }

    const endDate = formatDateValue(this.endDateControl.value);

    if (!endDate) {
      this.endDateControl.setErrors({ required: true });
      return;
    }

    this.dialogRef.close({ endDate } satisfies BouquetServiceActivationDialogResult);
  }
}

function today(): Moment {
  return moment().startOf('day');
}

function parseDateValue(value: string | null | undefined): Moment | null {
  if (!value) {
    return null;
  }

  const parsedDate = moment(value.substring(0, 10), 'YYYY-MM-DD', true);

  return parsedDate.isValid() ? parsedDate.startOf('day') : null;
}

function formatDateValue(value: unknown): string | null {
  if (moment.isMoment(value) && value.isValid()) {
    return value.format('YYYY-MM-DD');
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return formatDateParts(value.getFullYear(), value.getMonth() + 1, value.getDate());
  }

  if (typeof value === 'string') {
    return value.trim().substring(0, 10) || null;
  }

  if (value && typeof value === 'object' && 'year' in value && 'month' in value && 'date' in value) {
    const momentValue = value as { year: () => number; month: () => number; date: () => number };
    return formatDateParts(momentValue.year(), momentValue.month() + 1, momentValue.date());
  }

  return null;
}

function formatDateParts(year: number, month: number, day: number): string {
  return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}
